import { useEffect, useState } from "react";
import { Box, Container, Button } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { DJSimpleCodeBlock, ICodeBlock } from "../../main/components/codeblock";
import { IProgress, ISessionProps } from "../../main/components/IProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@appsflyer/fe-ui-core";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import transition from "../../main/components/transition";
import { amplitudeTrack_continue } from "../../services/utils";

const DL_implementReactNative = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState<IProgress[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [devKey, setDevKey] = useState<string>("");
  const [appId, setAppId] = useState<string>("");

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (location.state) {
      const state: ISessionProps = location.state as ISessionProps;
      setProgress(state?.progress);
      setAppId(state?.appID ?? "");
    }
    const lsDevKey = localStorage.getItem("devKey");
    if (lsDevKey) {
      setDevKey(JSON.parse(lsDevKey));
    }
  }, [location.state]);

  const handleClick = () => {
    const progressArr: IProgress[] = progress.slice();
    progressArr.push({
      id: "dl_dltype",
      text: "progress_DL_type",
      order: 4,
    });
    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
    };
    amplitudeTrack_continue(location.state);

    navigate(`/deeplink/${location.state?.os}/deeplinktype`, { state: progressState });
  };

  const code1 = `import appsFlyer from "react-native-appsflyer";`;
  const code2 = `appsFlyer.initSdk(
    {
        devKey: "${devKey}",
        isDebug: true,
        appId: "${appId}",
        onDeepLinkListener: true
    }
);`;
  const code3 = `appsFlyer.onDeepLink(res => {
    // will be implemented in the following steps.
})`;
  const code4 = `appsFlyer.onDeepLink(res => {
    if (res?.deepLinkStatus === 'FOUND') {      
      console.log('Deep Link was found');
    }
    else if (res?.deepLinkStatus === 'NOT_FOUND') {
      console.log('Deep Link was not found');
    }
})`;
  const code5 = `appsFlyer.getDeepLink(res => {
  if (res?.deepLinkStatus === 'FOUND') {
    console.log('Deep Link was found');
    console.log("The deep link data is: ", JSON.stringify(res, null, 2));
  }
  else if (res?.deepLinkStatus === 'NOT_FOUND') {
    console.log('Deep Link was not found');
  }
})`;
const code6 = `appsFlyer.getDeepLink(res => {
  if (res?.deepLinkStatus === 'FOUND') {
    console.log('Deep Link was found');
    console.log("The deep link data is: ", JSON.stringify(res, null, 2));
    const deepLinkDestination = res.data.deep_link_value;
    // Your code for handling the deeplink data
  }
  else if (res?.deepLinkStatus === 'NOT_FOUND') {
    console.log('Deep Link was not found');
  }
})`;
const code7 = `appsFlyer.getDeepLink(res => {
  if (res?.deepLinkStatus === 'FOUND') {
    console.log('Deep Link was found');
    console.log("The deep link data is: ", JSON.stringify(res, null, 2));
    const deepLinkDestination = res.data.deep_link_value;
    if (res.isDeferred){
      // Your code for deferred deep link
    } else {
      // Your code for direct deep link
    }
  }
  else if (res?.deepLinkStatus === 'NOT_FOUND') {
    console.log('Deep Link was not found');
  }
})`;


  const codeBlockArray: ICodeBlock[] = [
    {
      codePrimary: code1,
      language: "javascript",
      showLineNumbers: true
    },
    {
        codePrimary: code2,
        language: "javascript",
        showLineNumbers: true,
        highlight: "6"
    },
    {
        codePrimary: code3,
        language: "javascript",
        showLineNumbers: true
    },
    {
      codePrimary: code4,
      language: "javascript",
      showLineNumbers: true
    },
    {
      codePrimary: code5,
      language: "javascript",
      showLineNumbers: true,
      highlight: "4"
    },
    {
      codePrimary: code6,
      language: "javascript",
      showLineNumbers: true,
      highlight: "5-6"
    },
    {
      codePrimary: code7,
      language: "javascript",
      showLineNumbers: true,
      highlight: "6-9,10"
    }
  ];

  const steps = [
    {
        label: t("dl_page8_android_importCodeLibraries"),
        component: (
            <>
                <Typography>
                    {t("dl_implement_reactnative_step1_description")}
                </Typography>
                <DJSimpleCodeBlock {...codeBlockArray[0]} />
            </>
        ),
        shouldDisplayNavigationButtons: true
    },
    {
        label: t("dl_page10_android_step2Label"),
        component: (
            <>
                <DJSimpleCodeBlock {...codeBlockArray[1]} />
            </>
        ),
        shouldDisplayNavigationButtons: true
    },
    {
        label: t("dl_implement_reactnative_step3_label"),
        component: (
            <>
                <Typography>
                    <Trans
                        i18nKey="dl_implement_reactnative_step3_description"
                        components={{ code: <code /> , br: <br /> }}
                    />
                </Typography>
                <DJSimpleCodeBlock {...codeBlockArray[2]} />
            </>
        ),
        shouldDisplayNavigationButtons: true
    },
    {
      label: t("dl_page12_android_step4Label"),
      component: (
        <>
          <Typography>
            {t("dl_page12_android_retrieveUDLStatusInstructions")}
            <code style={{ marginLeft: '4px', marginRight: '4px' }}>deepLinkStatus</code>
            {t("dl_page12_android_retrieveUDLStatusInstructions_b")}
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[3]} />
        </>
      ),
      shouldDisplayNavigationButtons: true
    },
    {
      label: t("dl_page13_android_step5Label"),
      component: (
        <>
          <Typography>
            <Trans
              i18nKey="dl_implement_reactnative_step5_description"
              components={{ code: <code /> }}
            />
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[4]} />
        </>
      ),
      shouldDisplayNavigationButtons: true
    },
    {
      label: t("dl_page14_android_step6Label"),
      component: (
        <>
          <Typography>
            {t("dl_page14_android_useDeepLinkValueInstructions")}
            <code style={{ marginLeft: '4px', marginRight: '4px' }}>deep_link_value</code>
            {t("dl_page14_android_useDeepLinkValueInstructions_b")}
            <code style={{ marginLeft: '4px', marginRight: '4px' }}>deep_link_value</code>
            {t("dl_page14_android_useDeepLinkValueInstructions_c")}
            <code style={{ marginLeft: '4px', marginRight: '4px' }}>res.data.deep_link_value</code>
            {t("dl_page14_android_useDeepLinkValueInstructions_d")}
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[5]} />
        </>
      ),
      shouldDisplayNavigationButtons: true
    },
    {
      label: t("dl_page15_android_step7Label"),
      component: (
        <>
          <Typography>
            <code>isDeferred</code>{" "}
            {t("dl_page15_android_optionalCheckInstructions")}
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[6]} />
        </>
      ),
      shouldDisplayNavigationButtons: true
    }
  ];

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} marginBottom={-10} style={{ minHeight: "50vh" }}>
        <Box marginBottom={2}>
          <Typography variant="h1">
            {t("dl_page9_android_pageTitle")}
          </Typography>
          <Stepper
            activeStep={activeStep}
            style={{ border: "none" }}
            orientation="vertical"
          >
            {steps.map(
              ({ label, component, shouldDisplayNavigationButtons }) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {component}
                    {shouldDisplayNavigationButtons && (
                      <Box sx={{ mt: 2 }}>
                        {activeStep !== 0 && (
                          <Button
                            variant="text"
                            color="primary"
                            onClick={handleBack}
                          >
                            {t("general_Back")}
                          </Button>
                        )}
                        {activeStep !== steps.length - 1 && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                          >
                            {activeStep === steps.length - 1
                              ? t("general_Finish")
                              : t("general_Next")}
                          </Button>
                        )}
                      </Box>
                    )}
                  </StepContent>
                </Step>
              )
            )}
          </Stepper>
        </Box>
      </Box>

      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={() => handleClick()}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(DL_implementReactNative);

